// src/components/SOXLPullback.tsx — SOXL Pullback (Moderate) strategy tab

import { useState, useEffect, useCallback } from "react";
import LoadingSpinner from "./LoadingSpinner";
import TickerPrice from "./TickerPrice";
import { buildApiUrl } from "../api/etfApi";
import {
  getSoxlPullbackSignal,
  type SoxlPullbackSignalResponse,
  type SoxlPullbackParams,
  type SoxlPullbackScores,
} from "../api/soxlPullbackApi";

interface SOXLPullbackProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
}

function SOXLPullback({ enabled, onEnabledChange }: SOXLPullbackProps) {
  const [signal, setSignal] = useState<SoxlPullbackSignalResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [symbol, setSymbol] = useState("SOXL");

  // Ticker data box (same as Single ETF Classic)
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [previousPrice, setPreviousPrice] = useState<number | null>(null);
  const [preMarketPrice, setPreMarketPrice] = useState<number | null>(null);
  const [preMarketChange, setPreMarketChange] = useState<number | null>(null);
  const [postMarketPrice, setPostMarketPrice] = useState<number | null>(null);
  const [postMarketChange, setPostMarketChange] = useState<number | null>(null);
  const [isPrePostMarket, setIsPrePostMarket] = useState<boolean>(false);
  const [marketStatus, setMarketStatus] = useState<string>("Market Closed");
  const [semisGreen, setSemisGreen] = useState(false);
  const [divergenceSkip, setDivergenceSkip] = useState(false);
  const [divergenceThresholdPct, setDivergenceThresholdPct] = useState(-0.5);
  const [stopMode, setStopMode] = useState<"conservative" | "moderate">("moderate");
  const [riskPct, setRiskPct] = useState(0.75);
  const [twoTranche, setTwoTranche] = useState(false);
  const [takeProfitPct, setTakeProfitPct] = useState(60);
  const [targetMode, setTargetMode] = useState("max_prior_1_2r");
  const [tradeVehicle, setTradeVehicle] = useState<"shares" | "defined_risk_options">("shares");
  const [aggressiveness, setAggressiveness] = useState<"conservative" | "moderate" | "aggressive">("moderate");
  const [accountValue, setAccountValue] = useState(10000);

  const loadSignal = useCallback(async () => {
    try {
      setError(null);
      const params: SoxlPullbackParams = {
        symbol,
        semis_green_enabled: semisGreen,
        divergence_skip_enabled: divergenceSkip,
        divergence_threshold_pct: divergenceThresholdPct,
        stop_mode: stopMode,
        risk_pct: riskPct,
        two_tranche: twoTranche,
        take_profit_pct: takeProfitPct,
        target_mode: targetMode,
        trade_vehicle: tradeVehicle,
        aggressiveness,
        account_value_usd: accountValue,
      };
      const data = await getSoxlPullbackSignal(params);
      setSignal(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to load signal";
      setError(msg);
      setSignal(null);
    } finally {
      setLoading(false);
    }
  }, [
    symbol,
    semisGreen,
    divergenceSkip,
    divergenceThresholdPct,
    stopMode,
    riskPct,
    twoTranche,
    takeProfitPct,
    targetMode,
    tradeVehicle,
    aggressiveness,
    accountValue,
  ]);

  useEffect(() => {
    setLoading(true);
    loadSignal();
  }, [loadSignal]);

  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(loadSignal, 30000);
    return () => clearInterval(interval);
  }, [enabled, loadSignal]);

  // Market status (same as Single ETF Classic)
  useEffect(() => {
    const getMarketStatus = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const dayOfWeek = etTime.getDay();
      const timeInMinutes = etTime.getHours() * 60 + etTime.getMinutes();
      if (dayOfWeek === 0 || dayOfWeek === 6) return "Market Closed";
      if (timeInMinutes >= 240 && timeInMinutes < 570) return "Pre-Market";
      if (timeInMinutes >= 570 && timeInMinutes < 960) return "Market Open";
      if (timeInMinutes >= 960 && timeInMinutes < 1200) return "Post-Market";
      return "Market Closed";
    };
    setMarketStatus(getMarketStatus());
    const statusInterval = setInterval(() => setMarketStatus(getMarketStatus()), 60000);
    return () => clearInterval(statusInterval);
  }, []);

  // Reset price when symbol changes so we don't show stale price
  useEffect(() => {
    setCurrentPrice(null);
    setPreviousPrice(null);
    setPreMarketPrice(null);
    setPreMarketChange(null);
    setPostMarketPrice(null);
    setPostMarketChange(null);
    setIsPrePostMarket(false);
  }, [symbol]);

  // Fetch current price for ticker data box (same API as Single ETF Classic)
  useEffect(() => {
    const fetchCurrentPrice = async () => {
      try {
        const url = buildApiUrl(`api/current-price/${symbol}`);
        const response = await fetch(url);
        if (!response.ok) return;
        const priceData = await response.json();
        const now = new Date();
        const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
        const dayOfWeek = etTime.getDay();
        const timeInMinutes = etTime.getHours() * 60 + etTime.getMinutes();
        const isNormalMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 570 && timeInMinutes < 960);
        const isPreMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 240 && timeInMinutes < 570);
        const isPostMarketHours = (dayOfWeek !== 0 && dayOfWeek !== 6) && (timeInMinutes >= 960 && timeInMinutes < 1200);

        if (priceData.current_price != null) {
          setCurrentPrice(priceData.current_price);
        }
        if (priceData.previous_close != null) {
          setPreviousPrice(priceData.previous_close);
        }
        const hasPre = priceData.pre_market_price != null && !isNaN(priceData.pre_market_price);
        const hasPost = priceData.post_market_price != null && !isNaN(priceData.post_market_price);
        const isPrePost = priceData.is_pre_post_market === true || priceData.is_pre_post_market === "true" || priceData.is_pre_post_market === 1;
        const shouldShowPre = hasPre && !isNormalMarketHours && isPreMarketHours;
        const shouldShowPost = hasPost && !isNormalMarketHours && (isPostMarketHours || (isPrePost && !isPreMarketHours));
        if (shouldShowPre) {
          setPreMarketPrice(priceData.pre_market_price);
          setPreMarketChange(priceData.pre_market_change ?? null);
          setPostMarketPrice(null);
          setPostMarketChange(null);
          setIsPrePostMarket(true);
        } else if (shouldShowPost) {
          setPreMarketPrice(null);
          setPreMarketChange(null);
          setPostMarketPrice(priceData.post_market_price);
          setPostMarketChange(priceData.post_market_change ?? null);
          setIsPrePostMarket(true);
        } else {
          setPreMarketPrice(null);
          setPreMarketChange(null);
          setPostMarketPrice(null);
          setPostMarketChange(null);
          setIsPrePostMarket(false);
        }
      } catch {
        // keep existing price state
      }
    };
    fetchCurrentPrice();
    const isInTradingHours = () => {
      const now = new Date();
      const etTime = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
      const d = etTime.getDay();
      const t = etTime.getHours() * 60 + etTime.getMinutes();
      if (d === 0 || d === 6) return false;
      return (t >= 240 && t < 570) || (t >= 570 && t < 960) || (t >= 960 && t < 1200);
    };
    let interval: ReturnType<typeof setInterval> | null = null;
    if (isInTradingHours()) interval = setInterval(fetchCurrentPrice, 30000);
    return () => { if (interval) clearInterval(interval); };
  }, [symbol]);

  const filters = signal?.filters ?? {};
  const scores: Partial<SoxlPullbackScores> = signal?.scores ?? {};
  const setup = signal?.setup ?? {};
  const llm = signal?.llm_eval ?? {};
  const nextAction = signal?.next_action ?? "—";
  const flags = scores?.flags ?? {};
  const pullbackLen = scores?.pullback_len ?? -1;
  const pullbackInRange = pullbackLen >= 2 && pullbackLen <= 8;
  const qualityMin = 60;
  const confirmMin = 60;
  const pullbackQualityOk = (scores?.pullback_quality_score ?? 0) >= qualityMin;
  const confirmOk = (scores?.confirm_score ?? 0) >= confirmMin;
  const touchOk = flags?.touch_ok === true;
  const rsi60Ok = filters?.rsi_60m_over_max !== true;
  const entryValid = setup?.entry_valid === true;

  return (
    <div className="main-layout" style={{ gap: "14px", marginTop: "8px" }}>
      {/* Left: Controls */}
      <section className="panel panel-left">
        <div className="panel-inner">
          <h2 className="panel-title">SOXL Pullback (Moderate)</h2>
          <p className="panel-description">
            Intraday–multi-day momentum pullback. 15m execution, 60m trend. VWAP, EMA9/21, RSI, ATR.
          </p>

          <div className="field">
            <label className="field-label">Symbol</label>
            <input
              className="text-input"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              placeholder="SOXL"
            />
          </div>

          <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="pb-enabled"
              checked={enabled}
              onChange={(e) => onEnabledChange(e.target.checked)}
            />
            <label htmlFor="pb-enabled" className="field-label" style={{ marginBottom: 0 }}>
              Enable strategy (poll signal)
            </label>
          </div>

          <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="pb-semis-green"
              checked={semisGreen}
              onChange={(e) => setSemisGreen(e.target.checked)}
            />
            <label htmlFor="pb-semis-green" className="field-label" style={{ marginBottom: 0 }} title="Only allow LONG when SMH and NVDA are green on the day">
              Semis green filter (SMH &amp; NVDA &gt; 0 today)
            </label>
          </div>

          <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="pb-divergence"
              checked={divergenceSkip}
              onChange={(e) => setDivergenceSkip(e.target.checked)}
            />
            <label htmlFor="pb-divergence" className="field-label" style={{ marginBottom: 0 }} title="Skip new LONG when semis lag QQQ by more than threshold">
              Skip if semis diverge vs QQQ
            </label>
          </div>

          {divergenceSkip && (
            <div className="field">
              <label className="field-label">Divergence threshold D%</label>
              <input
                className="text-input"
                type="number"
                step="0.1"
                value={divergenceThresholdPct}
                onChange={(e) => setDivergenceThresholdPct(Number(e.target.value))}
              />
            </div>
          )}

          <div className="field">
            <label className="field-label">Stop mode</label>
            <select
              className="select-input"
              value={stopMode}
              onChange={(e) => setStopMode(e.target.value as "conservative" | "moderate")}
            >
              <option value="conservative">Conservative (VWAP-ATR / session low)</option>
              <option value="moderate">Moderate (swing low)</option>
            </select>
          </div>

          <div className="field">
            <label className="field-label">Risk per trade %</label>
            <input
              className="text-input"
              type="number"
              min={0.5}
              max={1}
              step={0.05}
              value={riskPct}
              onChange={(e) => setRiskPct(Number(e.target.value))}
            />
          </div>

          <div className="field" style={{ flexDirection: "row", alignItems: "center", gap: "8px" }}>
            <input
              type="checkbox"
              id="pb-two-tranche"
              checked={twoTranche}
              onChange={(e) => setTwoTranche(e.target.checked)}
            />
            <label htmlFor="pb-two-tranche" className="field-label" style={{ marginBottom: 0 }}>
              2-tranche entry (70% / 30%)
            </label>
          </div>

          <div className="field">
            <label className="field-label">Profit target mode</label>
            <select
              className="select-input"
              value={targetMode}
              onChange={(e) => setTargetMode(e.target.value)}
            >
              <option value="prior_high">Prior intraday high</option>
              <option value="1_2r">+1.2R to +1.5R</option>
              <option value="max_prior_1_2r">max(prior high, +1.2R)</option>
            </select>
          </div>

          <div className="field">
            <label className="field-label">Take-profit % at target1</label>
            <input
              className="text-input"
              type="number"
              min={50}
              max={70}
              value={takeProfitPct}
              onChange={(e) => setTakeProfitPct(Number(e.target.value))}
            />
          </div>

          <div className="field">
            <label className="field-label">Trade vehicle</label>
            <select
              className="select-input"
              value={tradeVehicle}
              onChange={(e) => setTradeVehicle(e.target.value as "shares" | "defined_risk_options")}
            >
              <option value="shares">Shares / ETF</option>
              <option value="defined_risk_options">Defined-risk options (suggestion)</option>
            </select>
          </div>

          <div className="field">
            <label className="field-label">Aggressiveness</label>
            <select
              className="select-input"
              value={aggressiveness}
              onChange={(e) => setAggressiveness(e.target.value as "conservative" | "moderate" | "aggressive")}
            >
              <option value="conservative">Conservative</option>
              <option value="moderate">Moderate</option>
              <option value="aggressive">Aggressive</option>
            </select>
          </div>

          <div className="field">
            <label className="field-label">Account value (USD)</label>
            <input
              className="text-input"
              type="number"
              min={0}
              value={accountValue}
              onChange={(e) => setAccountValue(Number(e.target.value))}
            />
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          <button className="primary-button" onClick={() => { setLoading(true); loadSignal(); }} disabled={loading}>
            {loading ? "Refreshing…" : "Refresh signal"}
          </button>
        </div>
      </section>

      {/* Right: Display */}
      <section className="panel panel-right">
        <div className="panel-inner">
          {loading && !signal && <LoadingSpinner label="Loading signal…" />}
          {!loading && !signal && (
            <>
              <p style={{ color: "var(--text-muted)", marginBottom: "12px" }}>No signal loaded. Click &quot;Refresh signal&quot; to load. When loaded, you will see:</p>
              <h3 className="panel-title" style={{ marginBottom: "4px" }}>Conditions to trade</h3>
              <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Six conditions (data, trend, open, VWAP, semis, divergence).</p>
              <div id="soxl-pullback-setup-gates">
                <h3 className="panel-title" style={{ marginBottom: "4px", marginTop: "12px" }}>Setup gates (all required for Entry valid)</h3>
                <p style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>Six gates: clear prior rally, pullback 2–8 bars, quality ≥60, touch OK, confirm ≥60, 60m RSI not overbought.</p>
              </div>
            </>
          )}
          {signal && (
            <>
              {/* Ticker data box — same as Single ETF Classic results */}
              <div className="card etf-card" style={{ marginBottom: "12px" }}>
                <div className="card-header-row">
                  <div>
                    <div className="etf-ticker">
                      {symbol}
                      <TickerPrice
                        currentPrice={currentPrice}
                        previousPrice={previousPrice}
                        preMarketPrice={preMarketPrice}
                        preMarketChange={preMarketChange}
                        postMarketPrice={postMarketPrice}
                        postMarketChange={postMarketChange}
                        isPrePostMarket={isPrePostMarket}
                      />
                    </div>
                    <div
                      style={{
                        fontSize: "0.65rem",
                        color: marketStatus === "Market Open" ? "#22c55e" : marketStatus === "Pre-Market" ? "#f59e0b" : marketStatus === "Post-Market" ? "#3b82f6" : "#6b7280",
                        fontWeight: 500,
                        marginTop: "4px",
                        marginBottom: "2px",
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        display: "block",
                        lineHeight: "1.2",
                        minHeight: "14px",
                      }}
                    >
                      {marketStatus}
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="panel-title" style={{ marginBottom: "4px" }}>Conditions to trade</h3>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                All items below should be green (PASS) for a valid entry. If any are red, the strategy is waiting for a better setup.
              </p>
              <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.35 }}>
                <strong>Note:</strong> This strategy looks for <em>short</em> pullbacks only: a strong ~1.5 hr move up, then a 30 min–2 hr dip (2–8 fifteen‑minute bars). Multi‑day selloffs (like a drop from 73 to 48 over several days) do not qualify — pullback length would exceed the allowed window.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px", fontSize: "0.9rem" }}>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.data_ok ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.data_ok ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Price data available</strong> — {filters.data_ok
                    ? `We have enough recent data to evaluate the setup.${signal?.data_meta?.provider ? ` (${signal.data_meta.provider})` : ""}`
                    : (() => {
                        const meta = signal?.data_meta as { provider?: string; bars_received?: number; bars_required?: number } | undefined;
                        const prov = meta?.provider ?? "Data source";
                        const got = meta?.bars_received;
                        const need = meta?.bars_required ?? 45;
                        return got != null
                          ? `Not enough recent price data: ${prov} returned ${got} session bars (need ${need}). Strategy prefers Polygon when configured.`
                          : "Not enough recent price data. Wait for the next update.";
                      })()}
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.trend_60m ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.trend_60m ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Hourly trend is bullish</strong> — {filters.trend_60m ? "The 1-hour chart agrees with the trade; we're not fighting the trend." : "The 1-hour trend isn't bullish yet. Strategy waits for alignment."}
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.past_open_avoid !== false ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.past_open_avoid !== false ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Past opening volatility</strong> — {filters.past_open_avoid !== false ? "We're past the first 10 minutes after the open (less chop)." : "Within the first 10 minutes after the open; strategy avoids this choppy period."}
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.vwap_atr_ok !== false ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.vwap_atr_ok !== false ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Price not stretched</strong> — {filters.vwap_atr_ok !== false ? "Price isn't too far above the session average; we're not chasing." : "Price is too far above the session average (risky to enter here)."}
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.semis_green !== false ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.semis_green !== false ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Semis sector positive</strong> — {filters.semis_green !== false ? "SMH and NVDA are both up on the day (or this filter is off)." : "SMH or NVDA is down today. Only applies if you turned on the \"Semis green\" filter."}
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: filters.divergence_ok !== false ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{filters.divergence_ok !== false ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Semis vs market OK</strong> — {filters.divergence_ok !== false ? "Semis aren't lagging the broad market too much (or this filter is off)." : "Semis are underperforming vs the market. Only applies if you turned on the \"Divergence\" filter."}
                </li>
              </ul>
              {[filters.smh_day_pct, filters.nvda_day_pct, filters.rs_vs_qqq].some((v) => v != null) && (
                <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                  {filters.smh_day_pct != null && (
                    <li style={{ marginBottom: "4px" }}>SMH today: {Number(filters.smh_day_pct).toFixed(2)}% (semiconductor ETF)</li>
                  )}
                  {filters.nvda_day_pct != null && (
                    <li style={{ marginBottom: "4px" }}>NVDA today: {Number(filters.nvda_day_pct).toFixed(2)}%</li>
                  )}
                  {filters.rs_vs_qqq != null && (
                    <li style={{ marginBottom: "4px" }}>{symbol} vs QQQ (relative strength): {Number(filters.rs_vs_qqq).toFixed(2)}%</li>
                  )}
                </ul>
              )}

              <div id="soxl-pullback-setup-gates" style={{ scrollMarginTop: "12px" }}>
                <h3 className="panel-title" style={{ marginBottom: "4px" }}>Setup gates (all required for Entry valid)</h3>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                <strong>Next Action</strong> becomes &quot;Entry valid&quot; only when <em>all six conditions above</em> pass <em>and</em> all of the following are true. If any gate below is FAIL, you will see Stand aside / Wait / Setup forming even when conditions above are green.
              </p>
              <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px", fontSize: "0.9rem" }}>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: scores.impulse_present ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{scores.impulse_present ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Clear prior rally</strong> — Last ~6 bars must show an up-move (≥1% gain, above EMAs, mostly green candles). If No, Next Action is &quot;Wait&quot;.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: pullbackInRange ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{pullbackInRange ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Pullback length 2–8 bars</strong> — Bars from the rally high to now must be between 2 and 8 ({pullbackLen >= 0 ? pullbackLen : "—"} bars). Outside that window = no valid pullback.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: pullbackQualityOk ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{pullbackQualityOk ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Pullback quality ≥ {qualityMin}</strong> — Score {scores.pullback_quality_score ?? "—"} (structure, near VWAP/EMA21, RSI, volume). Below {qualityMin} = Stand aside.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: touchOk ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{touchOk ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Touch OK</strong> — Price must have come near VWAP or EMA21 during the pullback (within tolerance). If not, you may see &quot;Setup forming&quot; or &quot;Stand aside&quot;.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: confirmOk ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{confirmOk ? "PASS" : "FAIL"}</span>
                  {" "}<strong>Confirmation score ≥ {confirmMin}</strong> — Current 15m bar must confirm the bounce (green, above prior high, etc.). Score {scores.confirm_score ?? "—"}; below {confirmMin} = no entry.
                </li>
                <li style={{ marginBottom: "6px" }}>
                  <span style={{ color: rsi60Ok ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{rsi60Ok ? "PASS" : "FAIL"}</span>
                  {" "}<strong>60m RSI not overbought</strong> — Hourly RSI must not be above the cap (e.g. 70). Overbought 60m = Stand aside.
                </li>
              </ul>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                <strong>Summary:</strong> Entry valid = {entryValid ? "Yes — all gates and conditions passed." : "No — one or more gates or conditions above are not met."}
              </p>
              </div>

              <h3 className="panel-title" style={{ marginBottom: "4px" }}>Setup quality scores</h3>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                Higher scores (60+) are better. The overall setup score combines the others; when it&apos;s 60+ and all conditions and setup gates above are green, the strategy shows &quot;Entry valid&quot;.
              </p>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                <strong>Time period:</strong> Scores use <strong>15-minute bars during regular session only</strong> (9:30 AM–4:00 PM ET). The prior up-move looks at the last 6 session bars (~90 min); the pullback is the 2–8 bars after that high (~30 min–2 hr). Data can span multiple days (up to 30 days of session bars). VWAP is today&apos;s session only.
              </p>
              <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginBottom: "10px", padding: "8px 10px", background: "var(--bg-subtle, rgba(255,255,255,0.04))", borderRadius: "6px", lineHeight: 1.5 }}>
                <strong style={{ color: "var(--text-main)" }}>Why scores are often 0</strong><br />
                The strategy only scores when a specific setup is present: a <strong>prior up-move</strong> (last 6 bars up at least ~1%, mostly green, above EMAs) followed by a <strong>pullback</strong> of 2–8 bars. If there&apos;s no clear rally in the last ~90 min, &quot;Prior up-move strength&quot; and &quot;Clear prior rally?&quot; are 0 / No, and &quot;Pullback quality&quot; is 0 because there&apos;s no valid pullback. &quot;Confirmation&quot; is based only on the <em>current</em> 15m bar (green, engulfing prior high, etc.); if that bar is red or weak, it&apos;s 0. So zeros are normal when the market isn&apos;t in this exact setup — the strategy is waiting for the pattern to form.
              </div>
              <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: "0 0 8px", lineHeight: 1.4 }}>
                <strong>Basis of each score (how it&apos;s calculated):</strong>
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "6px", marginBottom: "12px", fontSize: "0.9rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span>Prior up-move strength</span>
                  <strong>{scores.impulse_score ?? "—"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px", marginBottom: "4px" }}>Only scored when &quot;Clear prior rally?&quot; is Yes. Based on: gain over last 6 bars (up to 40 pts), EMA separation (20), strong green candles (20), volume vs average (20). If there&apos;s no rally meeting the rules (≥1% gain, price above EMA9/21, ≥50% green candles), this is 0.</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span>Pullback quality</span>
                  <strong>{scores.pullback_quality_score ?? "—"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px", marginBottom: "4px" }}>Only scored when pullback length is 2–8 bars from the impulse high. Based on: near VWAP/EMA21 (25), slowing sell pressure (20), RSI not too low (15), volume behavior (15), structure (25). Penalties for waterfall drop, long red streak, or RSI &lt; 40. If pullback is &lt;2 or &gt;8 bars, or no prior rally, this is 0.</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span>Confirmation</span>
                  <strong>{scores.confirm_score ?? "—"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px", marginBottom: "4px" }}>Based only on the <strong>current 15m bar</strong>: green candle (+30), close above prior bar&apos;s high / engulfing (+40), wide range vs ATR (+20), close above EMA9 (+10). Max 100. If the current bar is red or weak, this is 0.</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span><strong>Overall setup score</strong></span>
                  <strong>{scores.setup_score ?? "—"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px", marginBottom: "4px" }}>45% prior up-move + 45% pullback quality + 10% confirmation. So if impulse and pullback quality are 0, overall is at most 10 (from confirmation only).</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span>Pullback length (bars)</span>
                  <strong>{scores.pullback_len ?? "—"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px", marginBottom: "4px" }}>Bars from the high of the last 6 bars to now. Must be 2–8 for pullback quality to be scored; otherwise 0.</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
                  <span>Clear prior rally?</span>
                  <strong>{scores.impulse_present ? "Yes" : "No"}</strong>
                </div>
                <div style={{ fontSize: "0.8rem", color: "var(--text-muted)", marginTop: "-2px" }}>Yes only when last 6 bars: gain ≥1%, price above EMA9 and EMA21, and ≥50% of candles are green. Otherwise prior up-move strength is 0.</div>
              </div>

              <h3 className="panel-title" style={{ marginBottom: "8px" }}>Next action</h3>
              <p style={{ margin: "0 0 12px", fontSize: "1rem", fontWeight: 600, color: nextAction === "Entry valid" ? "#22c55e" : "var(--text-main)" }}>
                {nextAction}
              </p>

              {(signal.entry?.price ?? signal.stops?.stop_below ?? signal.targets?.target1) && (
                <>
                  <h3 className="panel-title" style={{ marginBottom: "8px" }}>Levels</h3>
                  <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px", fontSize: "0.9rem" }}>
                    {signal.entry?.price != null && <li>Entry: ${Number(signal.entry.price).toFixed(2)}</li>}
                    {signal.stops?.stop_below != null && <li>Stop below: ${Number(signal.stops.stop_below).toFixed(2)}</li>}
                    {signal.targets?.target1 != null && <li>Target1: ${Number(signal.targets.target1).toFixed(2)} ({signal.targets.take_profit_pct ?? 60}% off)</li>}
                  </ul>
                </>
              )}

              {signal.position_sizing?.suggested_shares != null && (
                <p style={{ margin: "0 0 12px", fontSize: "0.9rem" }}>
                  Suggested shares: <strong>{signal.position_sizing.suggested_shares}</strong> (risk {signal.position_sizing.risk_pct}%)
                </p>
              )}

              <h3 className="panel-title" style={{ marginBottom: "8px" }}>Summary Evaluation</h3>
              <div style={{ marginBottom: "12px", fontSize: "0.9rem" }}>
                <p style={{ margin: "0 0 4px" }}>Confidence: <strong>{(llm as { confidence_score?: number }).confidence_score ?? "—"}</strong>/100</p>
                <p style={{ margin: "0 0 4px" }}>Action: <strong>{(llm as { recommended_action?: string }).recommended_action ?? "—"}</strong></p>
                {(llm as { risk_flags?: string[] }).risk_flags?.length ? (
                  <p style={{ margin: "4px 0 0", color: "#f59e0b" }}>Risk flags: {(llm as { risk_flags?: string[] }).risk_flags?.join(", ") ?? ""}</p>
                ) : null}
                {(llm as { rationale?: string[] }).rationale?.length ? (
                  <ul style={{ margin: "4px 0 0", paddingLeft: "1.2rem" }}>
                    {((llm as { rationale?: string[] }).rationale ?? []).map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                ) : null}
              </div>

              {tradeVehicle === "defined_risk_options" && (() => {
                const opts = signal.options_overlay;
                if (opts == null) return null;
                return (
                  <>
                    <h3 className="panel-title" style={{ marginBottom: "8px" }}>Options overlay (approximate)</h3>
                    <div style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
                      <p style={{ margin: "0 0 4px" }}>{(opts as { note?: string }).note ?? ""}</p>
                      <p style={{ margin: "0 0 4px" }}>Suggested DTE: {(opts as { suggested_dte?: number }).suggested_dte ?? "—"}</p>
                      <p style={{ margin: "0 0 4px" }}>IV proxy: {(opts as { iv_proxy?: number }).iv_proxy ?? "—"}</p>
                    </div>
                  </>
                );
              })()}

              {signal.data_meta?.data_timestamp && (
                <p style={{ marginTop: "12px", fontSize: "0.8rem", color: "var(--text-muted)" }}>
                  Data: {String(signal.data_meta.provider)} @ {String(signal.data_meta.data_timestamp)}
                </p>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

export default SOXLPullback;
